import { AutoField } from "@/components/common/AutoField";
import { toOpts, useLookups } from "@/hooks/useLookups";
import { AU_STATES, CYCLES, ENTITIES, FEE_PAID_BY, MAX_HOURS_PER, PAYMENT_TERMS, PAY_SCHEDULES, opts } from "@/lib/constants";

function Section({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm p-5">
      <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4">{title}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">{children}</div>
    </section>
  );
}

export function OverviewTab({ c, save, readOnly }) {
  const lk = useLookups();
  const f = (field, label, type = "text", extra = {}) => (
    <AutoField key={field} field={field} label={label} type={type} value={c[field]} onSave={save} readOnly={readOnly} change={c.field_changes?.[field]} {...extra} />
  );
  return (
    <div className="space-y-5" data-testid="overview-tab">
      <Section title="Placement">
        {f("recruitment_company_id", "Recruitment company", "select", { options: toOpts(lk?.recruitment_companies, c.recruitment_company_id) })}
        {f("recruiter_id", "Recruiter", "person", { personType: "recruiter_contact" })}
        {f("employing_entity", "Employing entity", "select", { options: opts(ENTITIES) })}
        {c.employing_entity === "pte_company" && f("pte_company_id", "PTE company", "select", { options: toOpts(lk?.pte_companies, c.pte_company_id) })}
        {f("job_position_id", "Position", "select", { options: toOpts(lk?.job_positions, c.job_position_id) })}
        {f("job_location_id", "Location", "select", { options: toOpts(lk?.job_locations, c.job_location_id) })}
        {f("work_state", "Work state", "select", { options: AU_STATES.map((s) => ({ value: s, label: s })) })}
        {f("start_date", "Start date", "date")}
        {f("end_date", "End date", "date")}
        {f("first_timesheet_due", "First timesheet due", "date")}
        {f("first_pay_date", "First pay date", "date")}
        {f("last_pay_date", "Last pay date", "date")}
        {f("assigned_to", "Assigned to", "select", { options: toOpts(lk?.staff) })}
      </Section>
      <Section title="Pay & fees">
        {f("pay_schedule", "Pay schedule", "select", { options: opts(PAY_SCHEDULES) })}
        {f("payment_terms", "Payment terms", "select", { options: opts(PAYMENT_TERMS) })}
        {f("recruiter_payment_cycle", "Recruiter payment cycle", "select", { options: opts(CYCLES) })}
        {f("payme_fee_paid_by", "PayMe fee paid by", "select", { options: opts(FEE_PAID_BY) })}
        {f("recruitment_fee_amount", "Recruitment fee (AUD)", "money")}
        {f("recruitment_fee_percent", "Recruitment fee %", "money")}
        {f("max_hours", "Max hours", "number")}
        {f("max_hours_per", "Max hours per", "select", { options: opts(MAX_HOURS_PER) })}
      </Section>
      <Section title="Tax, super & compliance">
        {f("payroll_tax_by_payme", "Payroll tax paid by PayMe", "switch")}
        {f("workers_comp", "Workers comp", "switch")}
        {f("claims_tax_free_threshold", "Claims tax-free threshold", "switch")}
        {f("super_fund_id", "Super fund", "select", { options: toOpts(lk?.super_funds, c.super_fund_id) })}
        {f("super_member_number", "Super member number")}
        {f("lafha_eligible", "LAFHA eligible", "switch")}
        {f("notes", "Notes", "textarea", { className: "sm:col-span-2 xl:col-span-3" })}
      </Section>
    </div>
  );
}

export function LafhaTab({ c, save, readOnly }) {
  const f = (field, label, type) => <AutoField field={field} label={label} type={type} value={c[field]} onSave={save} readOnly={readOnly} change={c.field_changes?.[field]} />;
  return (
    <div data-testid="lafha-tab">
      <Section title="Living away from home allowance">
        {f("lafha_start", "LAFHA start", "date")}
        {f("lafha_end", "LAFHA end", "date")}
        {f("lafha_rent_amount", "Rent amount (AUD / week)", "money")}
        {f("lafha_food_amount", "Food amount (AUD / week)", "money")}
      </Section>
    </div>
  );
}
